import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { X } from 'lucide-react';
import type { CustomerRow, PaginatedResponse, ProviderSlug } from '@runik/shared';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { UsageView } from '../../../../components/usage/UsageView';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../components/ui/select';
import { DatePicker } from '../../../../components/ui/date-picker';
import { request } from '../../../../lib/auth/client';

const ALL = '__all__';

const PRESETS = [
  { key: '7d', label: 'Son 7 gün', days: 7 },
  { key: '14d', label: 'Son 14 gün', days: 14 },
  { key: '30d', label: 'Son 30 gün', days: 30 },
  { key: 'mtd', label: 'Bu ay', days: -1 }, // -1 = month-to-date
] as const;

/**
 * DatePicker local-zone Date'lerle çalışır. API filtreleri UTC bazlı snapshot'lara karşı
 * çalıştığı için kullanıcının seçtiği "Nisan 30" → UTC günü Nisan 30'a çeviririz.
 * Convention: from = inclusive (Apr 30 00:00 UTC), to = exclusive (May 1 00:00 UTC).
 */
function pickedToUTCStartISO(d: Date): string {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
}

function pickedToUTCEndExclusiveISO(d: Date): string {
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 1)).toISOString();
}

/** İki Date'in aynı takvim gününü gösterip göstermediği (local). */
function sameLocalDay(a: Date | undefined, b: Date | undefined): boolean {
  if (!a || !b) return a === b;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Default: bugün dahil son 30 gün (local takvim, DatePicker doğru göstersin). */
function defaultRange(): { from: Date; to: Date } {
  const today = new Date();
  const to = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 29);
  return { from, to };
}

function AdminUsagePage() {
  const [customerSel, setCustomerSel] = useState<string>(ALL);
  const [providerSel, setProviderSel] = useState<string>(ALL);
  const initial = useMemo(() => defaultRange(), []);
  const [fromDate, setFromDate] = useState<Date | undefined>(initial.from);
  const [toDate, setToDate] = useState<Date | undefined>(initial.to);

  const customers = useQuery({
    queryKey: ['customers', 'all-for-select'],
    queryFn: () => request<PaginatedResponse<CustomerRow>>('/api/customers?limit=100'),
  });

  const customerId = customerSel === ALL ? undefined : customerSel;
  const providerSlug = providerSel === ALL ? undefined : (providerSel as ProviderSlug);
  const customerName =
    customerSel === ALL
      ? null
      : customers.data?.data.find((c) => c.id === customerSel)?.companyName ?? null;

  const subtitleParts: string[] = [];
  if (customerName) subtitleParts.push(customerName);
  if (providerSlug) subtitleParts.push(providerSlug === 'openai' ? 'OpenAI' : 'Replicate');
  const subtitle =
    subtitleParts.length > 0
      ? `${subtitleParts.join(' · ')} kullanımı`
      : 'Tüm müşteriler ve sağlayıcılar';

  const applyPreset = (days: number) => {
    const now = new Date();
    const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let from: Date;
    if (days === -1) {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
    }
    setFromDate(from);
    setToDate(to);
  };

  const clearAll = () => {
    setCustomerSel(ALL);
    setProviderSel(ALL);
    const r = defaultRange();
    setFromDate(r.from);
    setToDate(r.to);
  };

  const isFiltered =
    customerSel !== ALL ||
    providerSel !== ALL ||
    !sameLocalDay(fromDate, initial.from) ||
    !sameLocalDay(toDate, initial.to);

  return (
    <>
      <PageHeader title="Kullanım" description={subtitle} />
      <div className="px-6 pt-6 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="customerFilter" className="text-xs text-slate-500">
              Müşteri
            </Label>
            <Select value={customerSel} onValueChange={setCustomerSel}>
              <SelectTrigger id="customerFilter" className="w-[220px]">
                <SelectValue placeholder="Müşteri seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tümü</SelectItem>
                {(customers.data?.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.companyName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="providerFilter" className="text-xs text-slate-500">
              Sağlayıcı
            </Label>
            <Select value={providerSel} onValueChange={setProviderSel}>
              <SelectTrigger id="providerFilter" className="w-[160px]">
                <SelectValue placeholder="Sağlayıcı seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Tümü</SelectItem>
                <SelectItem value="openai">OpenAI</SelectItem>
                <SelectItem value="replicate">Replicate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Başlangıç</Label>
            <div className="w-[200px]">
              <DatePicker value={fromDate} onChange={setFromDate} placeholder="Başlangıç" />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Bitiş</Label>
            <div className="w-[200px]">
              <DatePicker value={toDate} onChange={setToDate} placeholder="Bitiş" />
            </div>
          </div>
          {isFiltered && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="mb-0.5">
              <X className="h-3 w-3" /> Temizle
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button
              key={p.key}
              variant="outline"
              size="sm"
              onClick={() => applyPreset(p.days)}
              className="h-7 text-xs"
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>
      <UsageView
        customerId={customerId}
        providerSlug={providerSlug}
        from={fromDate ? pickedToUTCStartISO(fromDate) : undefined}
        to={toDate ? pickedToUTCEndExclusiveISO(toDate) : undefined}
      />
    </>
  );
}

export const Route = createFileRoute('/_app/admin/usage/')({ component: AdminUsagePage });
